import { defineStore } from 'pinia'
import { store } from '../index'
import { getMeApi, getMyPermissionsApi, logoutApi } from '@/api/auth'
import type { LoginPayload, MyPermissions, UserInfo } from '@/api/auth/types'
import type { PermissionCode } from '@/constants'
import { usePermissionStore } from './permission'
import { useTagsViewStore } from './tagsView'
import router, { resetRouter } from '@/router'

interface UserState {
  /** 原始 JWT（不含 Bearer 前缀） */
  token: string
  /** 登录接口返回的会话信息（含 expires_at 等） */
  session: LoginPayload | null
  userInfo: UserInfo | null
  /** 当前登录用户已聚合的权限码（去重） */
  permissionCodes: string[]
  /** 是否已成功加载过权限码（避免重复请求） */
  permissionsLoaded: boolean
  rememberMe: boolean
  rememberedPhone: string
  /** 登出流程防重入：避免 401 触发的 logout() 与自身递归 */
  loggingOut: boolean
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: '',
    session: null,
    userInfo: null,
    permissionCodes: [],
    permissionsLoaded: false,
    rememberMe: true,
    rememberedPhone: '',
    loggingOut: false
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.token && state.userInfo),
    /**
     * 命中任一所需权限码即视为通过；缺省或空数组视为通过（无限制）
     */
    hasPermission:
      (state) =>
      (required: PermissionCode | PermissionCode[] | undefined): boolean => {
        if (!required) return true
        const list = Array.isArray(required) ? required : [required]
        if (list.length === 0) return true
        return list.some((code) => state.permissionCodes.includes(code))
      }
  },

  actions: {
    setToken(payload: LoginPayload) {
      this.token = payload.token
      this.session = payload
    },
    setUserInfo(info: UserInfo) {
      this.userInfo = info
    },
    setPermissionCodes(codes: string[]) {
      this.permissionCodes = [...new Set(codes)]
      this.permissionsLoaded = true
    },
    rememberAccount(phone: string, remember: boolean) {
      this.rememberMe = remember
      this.rememberedPhone = remember ? phone : ''
    },

    /** 拉取并写入当前登录用户信息 */
    async loadProfile() {
      const me = await getMeApi()
      this.userInfo = me
      return me
    },

    /** 拉取并写入当前登录用户已聚合的权限码 */
    async loadPermissions() {
      const data: MyPermissions = await getMyPermissionsApi()
      this.setPermissionCodes(data.permission_codes)
      return data
    },

    clearSession() {
      this.token = ''
      this.session = null
      this.userInfo = null
      this.permissionCodes = []
      this.permissionsLoaded = false
      usePermissionStore().reset()
      useTagsViewStore().removeAllViews(false)
      resetRouter()
    },

    async logout() {
      // 防重入：transformResponse 看到 401 会调用 logout()，但 logout() 自身
      // 调用的 logoutApi() 也可能返回 401，从而再次触发 logout() 形成递归。
      if (this.loggingOut) return
      this.loggingOut = true
      try {
        if (this.token) {
          // 仅在持有 token 时才调后端 logout；空 token 调过去也会 401 → 又触发 logout
          try {
            await logoutApi()
          } catch {
            // 后端 logout 失败（如 token 已失效）也继续清本地
          }
        }
        this.clearSession()
        await router.replace('/login')
      } finally {
        this.loggingOut = false
      }
    }
  },

  persist: {
    key: 'vea-session-v2',
    pick: ['token', 'userInfo', 'permissionCodes', 'rememberMe', 'rememberedPhone']
  }
})

export const useUserStoreWithOut = () => useUserStore(store)